import { useEffect } from 'react';

/**
 * Generic hook that sets `document.title` and the `<meta name="description">`
 * content on mount and restores both previous values on unmount.
 *
 * If the meta description tag does not exist in the DOM at mount time, it is
 * created (and removed again on unmount — the element was absent beforehand).
 *
 * This hook is intentionally generic. It contains no webdev-specific logic
 * so that blog pages and other routes can adopt it later.
 *
 * Restore caveat: when `title`/`description` change while mounted (e.g. a
 * language switch), the effect re-runs and snapshots the CURRENT document
 * values as "previous" — so unmount restores the most recent pre-effect
 * state, not the values from before the first mount.
 *
 * Single-owner assumption: this hook assumes ONE mounted component owns the
 * document description at a time (true today — every route renders exactly one
 * page). Two simultaneously-mounted owners would each snapshot the other's value
 * as "previous", so a staggered unmount could restore an intermediate value.
 * Revisit if a layout ever mounts two meta owners at once.
 *
 * @param {string} title       - The page title to set (e.g. "Weboldal készítés | explainIT")
 * @param {string} description - The meta description content to set
 *
 * @example
 * // In a page component:
 * useDocumentMeta(t('webdev.seo.title'), t('webdev.seo.description'));
 */
function useDocumentMeta(title, description) {
  useEffect(() => {
    // --- document.title ---
    const previousTitle = document.title;
    document.title = title;

    // --- <meta name="description"> ---
    let metaEl = document.querySelector('meta[name="description"]');
    let wasCreated = false;

    if (!metaEl) {
      metaEl = document.createElement('meta');
      metaEl.setAttribute('name', 'description');
      document.head.appendChild(metaEl);
      wasCreated = true;
    }

    // null (not '') when the tag exists but carries no content attribute —
    // the two restore differently, so the distinction has to survive.
    const previousDescription = metaEl.getAttribute('content');
    metaEl.setAttribute('content', description);

    // --- Cleanup: restore previous state on unmount ---
    return () => {
      document.title = previousTitle;

      if (wasCreated) {
        // We created the tag — remove it on unmount
        metaEl.parentNode?.removeChild(metaEl);
      } else if (previousDescription === null) {
        // The tag was there but had no content attribute; restoring '' would
        // leave content="" behind, which is not the state we found.
        metaEl.removeAttribute('content');
      } else {
        metaEl.setAttribute('content', previousDescription);
      }
    };
  }, [title, description]);
}

export default useDocumentMeta;
